import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, catchError, filter, map, of } from 'rxjs';
import { MessageService } from 'primeng/api';

import { DisplayGroupsApi } from '../../../api/displayGroups/display-groups.api';
import { DisplayGroupModel } from '../models/display-group.model';
import { DisplayGroupTypeEnum } from '../../../api/displayGroups/enums/display-group-type.enum';
import { WebsocketAwareNeptuneService } from '../../../Bootstrap/services/websocket-aware-neptune.service';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { TextInputDialogModel } from '../../Dialogs/models/text-input-dialog.model';
import { TextInputDialogComponent } from '../../Dialogs/components/TextInputDialog/text-input-dialog.component';
import { ConfirmDialogModel } from '../../Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { DialogIoModel } from '../../../Core/Dialogs/models/dialog-io.model';
import { GroupSelectionDialogComponent } from '../../Dialogs/components/GroupSelectionDialog/group-selection-dialog.component';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { CommonApiErrorEnum } from '../../../api/genericApi/enums/common-api-error.enum';

@Injectable()
export class DisplayGroupsService {

    private subscriptions: Subscription[] = [];
    private currentType: DisplayGroupTypeEnum;
    private groups: DisplayGroupModel[] = [];
    private groups$: BehaviorSubject<DisplayGroupModel[]> = new BehaviorSubject(this.groups);


    constructor(
        private displayGroupsApi: DisplayGroupsApi,
        private webSocketEvents: WebsocketAwareNeptuneService,
        private coreDialogService: CoreDialogService,
        private localizationService: LocalizationService,
        private messageService: MessageService
    ) {
        this.subscriptions.push(
            this.webSocketEvents.getWebsocketMessages().pipe(
                filter(x => !!x))
                .subscribe(
                    message => {
                        if (message.resourceType === 'displaygroups') {
                            this.reloadGroups();
                        }
                    }
                )
        );
    }

    public getGroups() {
        return this.groups$;
    }

    /**
     * Sets current group type and creates subscription for type.
     * @param type Entity type
     * @returns Groups observable, sorted and including Uncategorized group.
     */
    public setTypeAndGetGroups(type: DisplayGroupTypeEnum): Observable<DisplayGroupModel[]> {
        this.currentType = type;
        this.displayGroupsApi.getLhpGroupsByType(type).pipe(
            map(groups => {
                this.groups = groups.map(group => new DisplayGroupModel(group));
                this.groups.sort((a, b) => a.name.localeCompare(b.name));
                this.groups$.next(this.groups);
            })
        ).subscribe();
        return this.groups$;
    }

    public reloadGroups() {
        this.setTypeAndGetGroups(this.currentType);
    }

    public openCreateGroupDialog(): void {
        const io: TextInputDialogModel = {
            inputs: {
                heading: 'Common.lhp.createGroupDialog.header',
                description: 'Common.lhp.createGroupDialog.inputLabel',
                placeholder: 'Common.lhp.createGroupDialog.inputPlaceholder',
                okLabel: 'Common.lhp.createGroupDialog.okButton',
                submit: (name: string) => {
                    const group = new DisplayGroupModel();
                    group.name = name;
                    return this.createGroup(group);
                }
            }
        };
        this.coreDialogService.open(TextInputDialogComponent, io);
    }

    public createGroup(group: DisplayGroupModel): Observable<number> {
        group.type = this.currentType;
        return this.displayGroupsApi.createGroup(group.toApiModel()).pipe(
            map((id) => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Common.lhp.groups.createGroup.success.summary'),
                    detail: this.localizationService.transform('Common.lhp.groups.createGroup.success.detail', {name: group.name})
                });
                this.reloadGroups();
                return id;
            }),
            catchError((err) => {
                if (err.error.details[0].code === CommonApiErrorEnum.duplicateName) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Common.lhp.groups.createGroup.error.summary'),
                        detail: this.localizationService.transform('Common.lhp.groups.createGroup.error.detail.duplicateName', {name: group.name})
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Common.lhp.groups.createGroup.error.summary'),
                        detail: this.localizationService.transform('Common.lhp.groups.createGroup.error.detail', {name: group.name})
                    });
                }

                throw err;
            })
        );
    }

    public renameGroup(group: DisplayGroupModel): void {
        const io: TextInputDialogModel = {
            inputs: {
                heading: 'Common.lhp.renameGroupDialog.header',
                description: 'Common.lhp.createGroupDialog.inputLabel',
                placeholder: 'Common.lhp.createGroupDialog.inputPlaceholder',
                okLabel: 'Common.lhp.renameGroupDialog.okButton',
                initialValue: group.name,
                submit: (name: string) => {
                    group.name = name;
                    return this.updateGroup(group);
                }
            }
        };
        this.coreDialogService.open(TextInputDialogComponent, io);
    }

    public updateGroup(group: DisplayGroupModel): Observable<void> {
        return this.displayGroupsApi.updateGroup(group.toApiModel()).pipe(
            map(() => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Common.lhp.groups.renameGroup.success.summary'),
                    detail: this.localizationService.transform('Common.lhp.groups.renameGroup.success.detail', {name: group.name})
                });
                this.reloadGroups();
            }),
            catchError((err) => {
                if(err.error.details[0].code === CommonApiErrorEnum.duplicateName) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Common.lhp.groups.renameGroup.error.summary'),
                        detail: this.localizationService.transform('Common.lhp.groups.createGroup.error.detail.duplicateName', {name: group.name})
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Common.lhp.groups.renameGroup.error.summary'),
                        detail: this.localizationService.transform('Common.lhp.groups.renameGroup.error.detail', {name: group.name})
                    });
                }
                throw err;
            })
        );
    }

    public confirmDeleteGroup(group: DisplayGroupModel): Observable<boolean> {
        return new Observable((subscriber) => {
            const io: ConfirmDialogModel = {
                inputs: {
                    heading: 'Common.lhp.deleteGroupDialog.header',
                    messageHtml: this.localizationService.transform('Common.lhp.deleteGroupDialog.description', {name: group.name}),
                    bottomMessage: 'Common.lhp.deleteGroupDialog.bottomMessage',
                    okLabel: 'Common.lhp.deleteGroupDialog.okButton'
                },
                outputs: {
                    dialogClose: (confirm: boolean) => {
                        if (confirm) {
                            this.deleteGroup(group);
                        }
                        subscriber.next(confirm);
                        subscriber.complete();
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, io);
        });

    }

    public deleteGroup(group: DisplayGroupModel): void {
        this.displayGroupsApi.deleteGroup(group.id).subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Common.lhp.groups.deleteGroup.success.summary'),
                    detail: this.localizationService.transform('Common.lhp.groups.deleteGroup.success.detail', {name: group.name})
                });
                this.reloadGroups();
            },
            error: (err) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Common.lhp.groups.deleteGroup.error.summary'),
                    detail: this.localizationService.transform('Common.lhp.groups.deleteGroup.error.detail', {name: group.name})
                });
            }
        });
    }

    /**
     * Allows selection of new group for item(s)
     * @param currentGroupId current group group to filter out of options (null to show all groups except Favorites)
     * @returns An obervable that either;
     * - next's the selected DisplayGroup (id property will be undefined for Uncategorized)
     * - completes the observable when cancelled
     */
    openGroupSelectionDialog(currentGroupId: number, showUncategorized: boolean = false): Observable<DisplayGroupModel> {
        let groupOptions = this.groups.slice();
        if (currentGroupId !== null) {
            groupOptions = groupOptions.filter(group => group.id !== currentGroupId);
        }
        if (showUncategorized) {
            const uncategorized = new DisplayGroupModel();
            uncategorized.name = this.localizationService.transform('Common.lhp.groups.uncategorized');
            groupOptions.push(uncategorized);
        }

        return new Observable<DisplayGroupModel>((subscriber) => {
            const io: DialogIoModel = {
                inputs: {
                    groupOptions$: of(groupOptions)
                },
                outputs: {
                    dialogClose: (group: DisplayGroupModel) => {
                        subscriber.next(group);
                        subscriber.complete();
                    },
                    dialogCancelled: () => {
                        subscriber.complete();
                    }
                }
            };
            this.coreDialogService.open(GroupSelectionDialogComponent, io);
        });
    }
}
